import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { initialState } from "./submission-state.model";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { CreateSubmissionDataRequest } from "../../models";
import { catchError, exhaustMap, of, pipe, tap } from "rxjs";
import { inject } from "@angular/core";
import {
  AppSchemasSubmissionsStatusEnum,
  BroadcastSubmissionRemindersToSuppliersApiV2MembersSuppliersSubmissionsBroadcastPost$Params,
  CreateSubmissionApiV2SubmissionsOrgPost$Params,
  DeleteSubmissionApiV2SubmissionsOrgIdDelete$Params,
  FilesUploaded,
  GetAllSubmissionsCyclesApiV2SubmissionsCycleOrgAllGet$Params,
  GetStatisticsForSubmissionsCyclesApiV2SubmissionsOrgSubmissionsCyclesStatisticsGet$Params,
  GetSubmissionApiV2SubmissionsOrgIdGet$Params,
  GetSubmissionFilesUploadedUrlsApiV2SubmissionsOrgIdFilesUrlGet$Params,
  GetSubmissionsByRecipientApiV2SubmissionsOrgSubmissionsGet$Params,
  GetSubmissionsOwnedBySecretariatApiV2SubmissionsOrgSecretariatSubmissionsGet$Params,
  GetSubmissionsSummaryBySubmissionsCycleApiV2SecretariatSubmissionsOrgSubmissionsCycleIdSubmissionsSummaryGet$Params,
  GetSubmissionsSummaryBySubmissionsCycleApiV2SubmissionsOrgSubmissionsCycleIdSubmissionsSummaryGet$Params,
  MembersV2Service,
  PageSubmissionsCycle,
  SubmissionsCycleV2Service,
  SubmissionsSecretariatV2Service,
  SubmissionsV2Service,
  SubmissionsWithSubmissionsData,
  UpdateSubmissionStatusApiV2SubmissionsOrgIdStatusPatch$Params,
} from "@rr/shared-data-access-api";
import { tapResponse } from "@ngrx/operators";
import { MessageService } from "primeng/api";
import { Router } from "@angular/router";
import { UtilUrlHelper } from "@rr/shared-util";
import { DashboardUrls } from "../../constants";

export const SubmissionStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withMethods(
    (
      store,
      submissionCycleService = inject(SubmissionsCycleV2Service),
      submissionService = inject(SubmissionsV2Service),
      submissionSecretariatService = inject(SubmissionsSecretariatV2Service),
      messageService = inject(MessageService),
      membersService = inject(MembersV2Service),
      router = inject(Router),
    ) => ({
      loadAllForms: rxMethod<GetAllSubmissionsCyclesApiV2SubmissionsCycleOrgAllGet$Params>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                listForms: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            return submissionCycleService.getAllSubmissionsCyclesApiV2SubmissionsCycleOrgAllGet(request).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: (forms: PageSubmissionsCycle | null) => {
                  patchState(store, { forms });
                },
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      listForms: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      createSubmission: rxMethod<CreateSubmissionApiV2SubmissionsOrgPost$Params>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                add: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            return submissionService.createSubmissionApiV2SubmissionsOrgPost(request).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: () => messageService.add({ detail: "Successfully created a submission.", severity: "success" }),
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      add: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      deleteSubmission: rxMethod<DeleteSubmissionApiV2SubmissionsOrgIdDelete$Params>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                remove: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            return submissionService.deleteSubmissionApiV2SubmissionsOrgIdDelete(request).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: () =>
                  messageService.add({
                    detail: "Successfully removed a submission.",
                    severity: "success",
                  }),
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      remove: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      loadSubmissionsByRecipient: rxMethod<GetSubmissionsByRecipientApiV2SubmissionsOrgSubmissionsGet$Params>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                listSubmissionsByRecipient: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            return submissionService.getSubmissionsByRecipientApiV2SubmissionsOrgSubmissionsGet(request).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: (response) => {
                  if (response) {
                    patchState(store, () => ({
                      tenantSubmissions: response,
                      selectedSubmission: null,
                    }));
                  }
                },
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      listSubmissionsByRecipient: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      loadSubmissionsBySecretariat:
        rxMethod<GetSubmissionsOwnedBySecretariatApiV2SubmissionsOrgSecretariatSubmissionsGet$Params>(
          pipe(
            tap(() =>
              patchState(store, (state) => ({
                isLoading: {
                  ...state.isLoading,
                  listSubmissionsBySecretariat: true,
                },
                error: null,
              })),
            ),
            exhaustMap((request) => {
              return submissionService
                .getSubmissionsOwnedBySecretariatApiV2SubmissionsOrgSecretariatSubmissionsGet(request)
                .pipe(
                  catchError((error) => {
                    patchState(store, { error });
                    return of(null);
                  }),
                  tapResponse({
                    next: (response) => {
                      if (response) {
                        patchState(store, () => ({
                          secretariatSubmissions: response,
                          selectedSubmission: null,
                        }));
                      }
                    },
                    error: (error) => console.error(error),
                    complete: () =>
                      patchState(store, (state) => ({
                        isLoading: {
                          ...state.isLoading,
                          listSubmissionsBySecretariat: false,
                        },
                      })),
                  }),
                );
            }),
          ),
        ),
      loadSubmissionById: rxMethod<GetSubmissionApiV2SubmissionsOrgIdGet$Params>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                loadSubmissionDetail: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            return submissionService.getSubmissionApiV2SubmissionsOrgIdGet(request).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: (response) => {
                  if (response) {
                    patchState(store, () => ({
                      selectedSubmission: response,
                    }));
                  }
                },
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      loadSubmissionDetail: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      updateSubmissionStatus: rxMethod<UpdateSubmissionStatusApiV2SubmissionsOrgIdStatusPatch$Params>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                loadSubmissionDetail: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            return submissionService.updateSubmissionStatusApiV2SubmissionsOrgIdStatusPatch(request).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: (response) => {
                  if (response) {
                    if (response?.status === AppSchemasSubmissionsStatusEnum.Completed) {
                      const data = store.selectedSubmission()?.submissionsData;
                      response.submissionsData = data as unknown as string;
                    }
                    patchState(store, (state) => ({
                      selectedSubmission: {
                        ...state.selectedSubmission,
                        ...(response as unknown as SubmissionsWithSubmissionsData),
                      },
                    }));
                  }
                },
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      loadSubmissionDetail: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      createOrUpdateSubmissionData: rxMethod<CreateSubmissionDataRequest>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                createOrUpdateSubmissionData: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            const newRequest = {
              id: request.id,
              org: request.org,
              body: request.body,
            };
            return submissionService.createSubmissionDataApiV2SubmissionsOrgIdDataPost(newRequest).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: (response) => {
                  if (response) {
                    patchState(store, (state) => ({
                      selectedSubmission: {
                        ...(state.selectedSubmission ?? ({} as SubmissionsWithSubmissionsData)),
                        submissionsData: response,
                      },
                    }));
                    if (request.showSuccessMessage) {
                      messageService.add({
                        detail: request.successMessage ?? "Successfully saved submission.",
                        severity: "success",
                      });
                    }
                  } else {
                    messageService.add({ detail: "Failed to save submission.", severity: "error" });
                  }
                },
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      createOrUpdateSubmissionData: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      retrieveSignedFiles: rxMethod<GetSubmissionFilesUploadedUrlsApiV2SubmissionsOrgIdFilesUrlGet$Params>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              isLoading: {
                ...state.isLoading,
                getSignedFiles: true,
              },
              error: null,
            })),
          ),
          exhaustMap((request) => {
            return submissionService.getSubmissionFilesUploadedUrlsApiV2SubmissionsOrgIdFilesUrlGet(request).pipe(
              catchError((error) => {
                patchState(store, { error });
                return of(null);
              }),
              tapResponse({
                next: (response) => {
                  if (response !== null) {
                    patchState(store, () => ({
                      signedFiles: response,
                    }));
                  } else {
                    messageService.add({
                      detail: `Failed to get file signed url.`,
                      severity: "error",
                    });
                  }
                },
                error: (error) => console.error(error),
                complete: () =>
                  patchState(store, (state) => ({
                    isLoading: {
                      ...state.isLoading,
                      getSignedFiles: false,
                    },
                  })),
              }),
            );
          }),
        ),
      ),
      loadStatistics:
        rxMethod<GetStatisticsForSubmissionsCyclesApiV2SubmissionsOrgSubmissionsCyclesStatisticsGet$Params>(
          pipe(
            tap(() =>
              patchState(store, (state) => ({
                isLoading: {
                  ...state.isLoading,
                  getStatistics: true,
                },
                upstreamSummary: null,
                secretariatSummary: null,
                error: null,
              })),
            ),
            exhaustMap((request) => {
              return submissionService
                .getStatisticsForSubmissionsCyclesApiV2SubmissionsOrgSubmissionsCyclesStatisticsGet(request)
                .pipe(
                  catchError((error) => {
                    patchState(store, { error });
                    return of(null);
                  }),
                  tapResponse({
                    next: (response) => {
                      if (response) {
                        patchState(store, { statistics: response });
                      } else {
                        messageService.add({ detail: "Failed to load submission statistics.", severity: "error" });
                      }
                    },
                    error: (error) => console.error(error),
                    complete: () =>
                      patchState(store, (state) => ({
                        isLoading: {
                          ...state.isLoading,
                          getStatistics: false,
                        },
                      })),
                  }),
                );
            }),
          ),
        ),
      loadUpstreamSummary:
        rxMethod<GetSubmissionsSummaryBySubmissionsCycleApiV2SubmissionsOrgSubmissionsCycleIdSubmissionsSummaryGet$Params>(
          pipe(
            tap(() =>
              patchState(store, (state) => ({
                isLoading: {
                  ...state.isLoading,
                  getSummary: true,
                },
                error: null,
              })),
            ),
            exhaustMap((request) => {
              return submissionService
                .getSubmissionsSummaryBySubmissionsCycleApiV2SubmissionsOrgSubmissionsCycleIdSubmissionsSummaryGet(
                  request,
                )
                .pipe(
                  catchError((error) => {
                    patchState(store, { error });
                    return of(null);
                  }),
                  tapResponse({
                    next: (response) => {
                      if (response) {
                        patchState(store, { upstreamSummary: response });
                      } else {
                        messageService.add({ detail: "Failed to load submission summary.", severity: "error" });
                      }
                    },
                    error: (error) => console.error(error),
                    complete: () =>
                      patchState(store, (state) => ({
                        isLoading: {
                          ...state.isLoading,
                          getSummary: false,
                        },
                      })),
                  }),
                );
            }),
          ),
        ),
      loadSecretariatSummary:
        rxMethod<GetSubmissionsSummaryBySubmissionsCycleApiV2SecretariatSubmissionsOrgSubmissionsCycleIdSubmissionsSummaryGet$Params>(
          pipe(
            tap(() =>
              patchState(store, (state) => ({
                isLoading: {
                  ...state.isLoading,
                  getSummary: true,
                },
                error: null,
              })),
            ),
            exhaustMap((request) => {
              return submissionSecretariatService
                .getSubmissionsSummaryBySubmissionsCycleApiV2SecretariatSubmissionsOrgSubmissionsCycleIdSubmissionsSummaryGet(
                  request,
                )
                .pipe(
                  catchError((error) => {
                    patchState(store, { error });
                    return of(null);
                  }),
                  tapResponse({
                    next: (response) => {
                      if (response) {
                        patchState(store, { secretariatSummary: response });
                      } else {
                        messageService.add({ detail: "Failed to load submission summary.", severity: "error" });
                      }
                    },
                    error: (error) => console.error(error),
                    complete: () =>
                      patchState(store, (state) => ({
                        isLoading: {
                          ...state.isLoading,
                          getSummary: false,
                        },
                      })),
                  }),
                );
            }),
          ),
        ),
      broadcastReminder:
        rxMethod<BroadcastSubmissionRemindersToSuppliersApiV2MembersSuppliersSubmissionsBroadcastPost$Params>(
          pipe(
            tap(() =>
              patchState(store, (state) => ({
                isLoading: {
                  ...state.isLoading,
                  broadcastReminder: true,
                },
                error: null,
              })),
            ),
            exhaustMap((request) => {
              return membersService
                .broadcastSubmissionRemindersToSuppliersApiV2MembersSuppliersSubmissionsBroadcastPost(request)
                .pipe(
                  catchError((error) => {
                    patchState(store, { error });
                    return of(null);
                  }),
                  tapResponse({
                    next: (response) => {
                      if (response) {
                        patchState(store, { reminderEmail: response });
                        if ((response.skippedOrgs ?? []).length > 0) {
                          // The rest were still reminded; the dialog names the organisations left out.
                          messageService.add({
                            detail: "Your message was broadcast, but some organisations could not be reached.",
                            severity: "warn",
                          });
                        } else {
                          messageService.add({
                            detail: "You have successfully broadcast your message.",
                            severity: "success",
                          });
                        }
                      } else {
                        messageService.add({ detail: "Failed to broadcast reminder.", severity: "error" });
                      }
                    },
                    error: (error) => console.error(error),
                    complete: () =>
                      patchState(store, (state) => ({
                        isLoading: {
                          ...state.isLoading,
                          broadcastReminder: false,
                        },
                      })),
                  }),
                );
            }),
          ),
        ),
      setRefreshSubmissions(refreshSubmissions: boolean) {
        patchState(store, { refreshSubmissions });
      },
      setSelectedSubmission(selectedSubmission: SubmissionsWithSubmissionsData | null) {
        patchState(store, { selectedSubmission });
      },
      clearSignedFiles() {
        patchState(store, { signedFiles: null });
      },
      clearSelectedSubmission() {
        if (!UtilUrlHelper.matchUrl(router.url, [DashboardUrls.SubmissionDetail])) {
          patchState(store, { selectedSubmission: null });
        }
      },
      clearReminderEmail() {
        patchState(store, { reminderEmail: null });
      },
      clearStatistics() {
        patchState(store, { statistics: null });
      },
      clearSummary() {
        patchState(store, { secretariatSummary: null, upstreamSummary: null });
      },
      updateSelectedSubmissionFiles(files: FilesUploaded[]) {
        patchState(store, (state) => ({
          selectedSubmission: {
            ...(state.selectedSubmission ?? ({} as SubmissionsWithSubmissionsData)),
            filesUploaded: [...[], ...files],
          },
        }));
      },
    }),
  ),
);
